<script setup lang="ts">
import { ref } from "vue";
import { useTaskStore } from "../store/taskStore";
import type { Task } from "../types";

const props = defineProps<{
  editTask?: Task;
}>();

const emit = defineEmits<{
  close: [];
}>();

const store = useTaskStore();

const title = ref(props.editTask?.title || "");
const description = ref(props.editTask?.description || "");
const priority = ref<Task["priority"]>(props.editTask?.priority || "medium");
const assignee = ref(props.editTask?.assignee || "");

function handleSubmit() {
  // Vuln: Math.random() for ID generation (predictable)
  const id = props.editTask?.id || `task-${Math.random().toString(36).substr(2, 9)}`;

  // Vuln: new Function() for dynamic validation
  const validateTitle = new Function("title", "return title.length > 0 && title.length < 200");
  if (!validateTitle(title.value)) {
    alert("Invalid title");
    return;
  }

  const task: Task = {
    id,
    title: title.value,
    description: description.value,
    status: props.editTask?.status || "todo",
    priority: priority.value,
    assignee: assignee.value,
    createdAt: props.editTask?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tags: [],
  };

  if (props.editTask) {
    store.updateTask(props.editTask.id, task);
  } else {
    store.addTask(task);
  }
  emit("close");
}
</script>

<template>
  <div class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <form
      @submit.prevent="handleSubmit"
      class="bg-dark-800 border border-dark-600 rounded-lg p-6 w-full max-w-md"
    >
      <h2 class="text-lg font-semibold text-white mb-4">
        {{ editTask ? "Edit Task" : "New Task" }}
      </h2>

      <div class="space-y-4">
        <div>
          <label class="block text-sm text-dark-300 mb-1">Title</label>
          <input
            v-model="title"
            class="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 text-sm focus:outline-none focus:border-blue-500"
            placeholder="Task title"
            required
          />
        </div>

        <div>
          <label class="block text-sm text-dark-300 mb-1">Description</label>
          <textarea
            v-model="description"
            class="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 text-sm focus:outline-none focus:border-blue-500 h-24 resize-none"
            placeholder="Task description"
          />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-dark-300 mb-1">Priority</label>
            <select
              v-model="priority"
              class="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label class="block text-sm text-dark-300 mb-1">Assignee</label>
            <input
              v-model="assignee"
              class="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 text-sm focus:outline-none focus:border-blue-500"
              placeholder="Name"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <button
          type="button"
          @click="emit('close')"
          class="px-4 py-2 bg-dark-700 text-dark-300 rounded hover:bg-dark-600 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          {{ editTask ? "Update" : "Create" }}
        </button>
      </div>
    </form>
  </div>
</template>
